import { type ReactNode } from 'react'
import {
  NoStoresIllustration,
  NoDeliveryIllustration,
  LocationIllustration,
  NetworkErrorIllustration,
  EmptySearchIllustration,
  ClosedIllustration,
} from './ui/Illustrations'

interface EmptyStateProps {
  icon?: string
  illustration?: ReactNode
  title: string
  description: string
  primaryAction?: {
    label: string
    onClick: () => void
  }
  secondaryAction?: {
    label: string
    onClick: () => void
  }
}

export function EmptyState({
  icon,
  illustration,
  title,
  description,
  primaryAction,
  secondaryAction,
}: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6 animate-fade-in">
      {illustration ? (
        <div className="mb-6">{illustration}</div>
      ) : icon ? (
        <div className="size-20 flex items-center justify-center rounded-full bg-surface-tertiary mb-6">
          <span className="text-4xl">{icon}</span>
        </div>
      ) : null}
      
      <h2 className="text-xl/7 font-semibold text-text-primary mb-2">
        {title}
      </h2>
      
      <p className="text-text-secondary mb-8 max-w-xs">
        {description}
      </p>

      {primaryAction && (
        <button
          onClick={primaryAction.onClick}
          className="
            w-full max-w-xs py-3 px-6
            bg-brand-primary text-white
            font-semibold rounded-xl
            shadow-card
            hover:shadow-card-hover
            transition-all duration-200
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
            active:scale-[0.98]
          "
        >
          {primaryAction.label}
        </button>
      )}

      {secondaryAction && (
        <button
          onClick={secondaryAction.onClick}
          className="
            mt-4 py-2 px-4
            text-brand-primary font-medium
            hover:underline
            focus-visible:outline-none focus-visible:underline
          "
        >
          {secondaryAction.label}
        </button>
      )}
    </div>
  )
}

// Pre-configured empty states

export function LocationPermissionDenied({ 
  onEnableSettings, 
  onContinueWithout 
}: { 
  onEnableSettings: () => void
  onContinueWithout: () => void 
}) {
  return (
    <EmptyState
      illustration={<LocationIllustration />}
      title="Location access needed"
      description="We use your location to show nearby stores and calculate accurate distances."
      primaryAction={{
        label: 'Enable in Settings',
        onClick: onEnableSettings,
      }}
      secondaryAction={{
        label: 'Continue without location',
        onClick: onContinueWithout,
      }}
    />
  )
}

export function NoStoresFound({ 
  onNotifyMe, 
  onViewAll 
}: { 
  onNotifyMe: () => void
  onViewAll: () => void 
}) {
  return (
    <EmptyState
      illustration={<NoStoresIllustration />}
      title="No stores in your area"
      description="We don't have any locations near you yet, but we're expanding!"
      primaryAction={{
        label: 'Notify me when available',
        onClick: onNotifyMe,
      }}
      secondaryAction={{
        label: 'View all locations',
        onClick: onViewAll,
      }}
    />
  )
}

export function NoDeliveryAvailable({ 
  onSwitchToPickup, 
  onTryDifferentAddress 
}: { 
  onSwitchToPickup: () => void
  onTryDifferentAddress: () => void 
}) {
  return (
    <EmptyState
      illustration={<NoDeliveryIllustration />}
      title="We don't deliver to this address"
      description="But you can still pick up from one of our nearby locations."
      primaryAction={{
        label: 'Switch to Pickup',
        onClick: onSwitchToPickup,
      }}
      secondaryAction={{
        label: 'Try a different address',
        onClick: onTryDifferentAddress,
      }}
    />
  )
}

export function AllStoresClosed({ 
  onPreOrder, 
  onSetReminder 
}: { 
  onPreOrder: () => void
  onSetReminder: () => void 
}) {
  return (
    <EmptyState
      illustration={<ClosedIllustration />}
      title="All stores are currently closed"
      description="Our nearest store opens at 11:00 AM tomorrow."
      primaryAction={{
        label: 'Pre-order for tomorrow',
        onClick: onPreOrder,
      }}
      secondaryAction={{
        label: 'Set a reminder to order',
        onClick: onSetReminder,
      }}
    />
  )
}

export function NetworkError({ 
  onRetry 
}: { 
  onRetry: () => void 
}) {
  return (
    <EmptyState
      illustration={<NetworkErrorIllustration />}
      title="Connection problem"
      description="We couldn't load store information. Check your connection and try again."
      primaryAction={{
        label: 'Try again',
        onClick: onRetry,
      }}
    />
  )
}

export function NoSearchResults({ 
  query, 
  onClearFilters 
}: { 
  query: string
  onClearFilters: () => void 
}) {
  return (
    <EmptyState
      illustration={<EmptySearchIllustration />}
      title="No stores found"
      description={`We couldn't find any stores matching "${query}". Try a different search term or clear your filters.`}
      primaryAction={{
        label: 'Clear filters',
        onClick: onClearFilters,
      }}
    />
  )
}
